/**
 * Every assessment of a hypothesis, newest first (the server sends them oldest first): verdict
 * and confidence, who and when, the rationale and what it cites. Nothing here edits one.
 */
import { Bell, FileText, FlaskConical, Network } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import type {
  EvidenceKind,
  EvidenceRefOut,
  HypothesisAssessment,
} from '@/types/analytic-hypothesis'
import { formatDateTimeLocalized } from '@/utils/date'
import { VERDICT_BADGE } from './strip'

const COPY = 'analyticHypotheses'

const KIND_ICON: Record<EvidenceKind, typeof FileText> = {
  investigation: Network,
  document: FileText,
  alert: Bell,
  research_run: FlaskConical,
}

export interface AssessmentListProps {
  /** Oldest first, as the server sends them. */
  assessments: readonly HypothesisAssessment[]
  /** Where a cited item opens; null when it has no page of its own. */
  evidenceHref: (ref: EvidenceRefOut) => string | null
}

function Evidence({ item, href }: { item: EvidenceRefOut; href: string | null }) {
  const { t } = useTranslation()
  const Icon = KIND_ICON[item.kind]
  const kind = t(`${COPY}.assessment.kinds.${item.kind}`)
  const body = (
    <>
      <Icon aria-hidden className="size-3 shrink-0 text-muted-foreground" />
      <span className="truncate">{item.label ?? kind}</span>
    </>
  )
  if (!href) {
    return (
      <Badge variant="outline" className="h-6 max-w-72" title={kind}>
        {body}
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="h-6 max-w-72" title={kind} asChild>
      <Link to={href}>{body}</Link>
    </Badge>
  )
}

function Entry({
  assessment,
  evidenceHref,
}: { assessment: HypothesisAssessment } & Pick<AssessmentListProps, 'evidenceHref'>) {
  const { t, i18n } = useTranslation()
  return (
    <article className="flex flex-col gap-2 rounded-lg border border-border/60 bg-card/60 p-3">
      <header className="flex flex-wrap items-center gap-2">
        <Badge variant={VERDICT_BADGE[assessment.verdict]}>
          {t(`${COPY}.verdict.${assessment.verdict}`)}
        </Badge>
        <span className="text-xs text-foreground/85">
          {t(`${COPY}.confidence.${assessment.confidence}`)}
        </span>
        <span className="flex-1" />
        <span className="text-2xs text-muted-foreground">
          {assessment.author_name} ·{' '}
          <time dateTime={assessment.created_at} className="tabular-nums">
            {formatDateTimeLocalized(assessment.created_at, i18n.language)}
          </time>
        </span>
      </header>
      <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
        {assessment.rationale}
      </p>
      {assessment.evidence.length > 0 && (
        <ul aria-label={t(`${COPY}.assessment.evidence`)} className="flex flex-wrap gap-1">
          {assessment.evidence.map(item => (
            <li key={`${item.kind}:${item.id}`}>
              <Evidence item={item} href={evidenceHref(item)} />
            </li>
          ))}
        </ul>
      )}
    </article>
  )
}

export function AssessmentList({ assessments, evidenceHref }: AssessmentListProps) {
  const { t } = useTranslation()
  if (assessments.length === 0) {
    return <p className="text-sm text-muted-foreground">{t(`${COPY}.detail.noAssessments`)}</p>
  }
  return (
    <ol className="flex flex-col gap-2">
      {[...assessments].reverse().map(assessment => (
        <li key={assessment.id}>
          <Entry assessment={assessment} evidenceHref={evidenceHref} />
        </li>
      ))}
    </ol>
  )
}
